import { Component, Inject } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { UserService } from 'src/app/services/user.service';
import { extractApiErrorMessage } from 'src/app/shared/utils/extract-api-error-message.util';
import { OrderListDefaults, User } from 'src/typings';
import { emptyOrderDefaults, ORDER_DEFAULT_STATUS_KEYS } from './order-list-defaults';

export interface OrderDefaultsDialogData {
  user: User;
  current: OrderListDefaults;
  labels: Record<string, string>;
}

@Component({
  selector: 'app-order-defaults-dialog',
  templateUrl: './order-defaults-dialog.component.html',
  styleUrls: ['./order-defaults-dialog.component.scss']
})
export class OrderDefaultsDialogComponent {
  draft: OrderListDefaults = emptyOrderDefaults();
  warehouseControl = new UntypedFormControl(null);
  preset: 'cashier' | 'manager' | 'current' = 'current';
  saving = false;
  error = '';

  constructor(@Inject(MAT_DIALOG_DATA) public data: OrderDefaultsDialogData,
    private ref: MatDialogRef<OrderDefaultsDialogComponent>, private users: UserService) {
    this.selectPreset('current');
  }

  selectPreset(preset: 'cashier' | 'manager' | 'current') {
    if (this.saving) return;
    this.preset = preset;
    this.error = '';
    this.draft = preset === 'current' ? JSON.parse(JSON.stringify(this.data.current)) :
      emptyOrderDefaults(preset === 'manager' ? 'me' : 'all');
    if (preset === 'cashier') {
      const warehouse = this.data.current.warehouse || this.data.user.defaultWarehouse;
      this.draft.warehouse = warehouse ? { id: warehouse.id, description: warehouse.description } : null;
    }
    this.warehouseControl.setValue(this.draft.warehouse);
  }

  get validWarehouse(): boolean {
    const value = this.warehouseControl.value;
    return (value === null || value === '') ? this.preset !== 'cashier' :
      Number.isSafeInteger(value?.id) && value.id > 0;
  }

  get managerLabel(): string {
    return this.draft.manager === 'me' ? 'Только мои заказы' :
      this.draft.manager === 'all' ? 'Все менеджеры' : this.draft.manager.name;
  }

  get statusLabels(): string[] {
    return ORDER_DEFAULT_STATUS_KEYS.reduce<string[]>((labels, key) => labels.concat(
      this.draft[key].map(value => this.data.labels[`${key}:${value}`] || value)), []);
  }

  async save(remove = false): Promise<void> {
    if (this.saving || (!remove && !this.validWarehouse)) return;
    this.saving = true;
    this.error = '';
    this.ref.disableClose = true;
    this.warehouseControl.disable({ emitEvent: false });
    const warehouse = this.warehouseControl.value;
    const defaults = remove ? null : { ...this.draft,
      warehouse: warehouse?.id ? { id: warehouse.id, description: warehouse.description } : null };
    try {
      await firstValueFrom(this.users.setOrderListDefaults(defaults, this.data.user.id));
      this.ref.close({ saved: true });
    } catch (error) {
      this.error = extractApiErrorMessage(error, 'Не удалось сохранить настройки. Попробуйте ещё раз.');
    } finally {
      this.saving = false;
      this.ref.disableClose = false;
      this.warehouseControl.enable({ emitEvent: false });
    }
  }
}
